/**
 * AdminDanceChartsManager - Manages dance chart sources (CopperKnob, LineDancer)
 * Extracted from AdminPage.js for better maintainability
 */
import React, { useState, useEffect } from 'react';
import { 
  Trash2, RefreshCw, Pencil, Plus, Save, Check, X, 
  ExternalLink, Upload, Download, ChevronLeft, ChevronRight,
  Music, Trophy, Globe, Loader2, Search
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { toast } from 'sonner';

import { API_URL } from '../../utils/api';

function DanceChartsManager() {
  const [activeSource, setActiveSource] = useState('copperknob');
  const [activeTab, setActiveTab] = useState('top20'); // 'top20' or 'djdb'
  const [dances, setDances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [editingDance, setEditingDance] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showBookmarklet, setShowBookmarklet] = useState(false);
  const [showEldImport, setShowEldImport] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [eldBulkText, setEldBulkText] = useState('');
  const [eldSaving, setEldSaving] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  
  // DJ Database state
  const [djDbDances, setDjDbDances] = useState([]);
  const [djDbCount, setDjDbCount] = useState(0);
  const [djDbLastUpdated, setDjDbLastUpdated] = useState(null);
  const [djDbLoading, setDjDbLoading] = useState(false);
  const [djDbBulkText, setDjDbBulkText] = useState('');
  const [djDbSaving, setDjDbSaving] = useState(false);
  
  // Bookmarklet code for extracting dances from CopperKnob Most Popular
  const copperknobBookmarklet = "javascript:(function(){var items=document.querySelectorAll('.listitem');var r=[];var n=0;items.forEach(function(e){n++;var nameEl=e.querySelector('.listTitleColor1');var name=nameEl?nameEl.textContent.trim():'';var choreoEl=e.querySelector('.listTitleColor2');var choreo='';if(choreoEl){var cText=choreoEl.textContent.trim();var cMatch=cText.match(/^([^-]+?)\\s*-\\s*(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)/);choreo=cMatch?cMatch[1].trim():cText.split('-')[0].trim();}var a=e.querySelector('a[href*=\"/stepsheets/\"]');var url=a?a.href:'';var info=e.querySelector('.listIcons');var t=info?info.textContent:'';var ct=t.match(/(\\d+)\\s*Count/);var wl=t.match(/(\\d+)\\s*Wall/);var lv='Improver';['Absolute Beginner','High Beginner','Beginner','High Improver','Improver','Easy Intermediate','Intermediate','Advanced'].forEach(function(l){if(t.includes(l))lv=l;});var mu=t.match(/Music:\\s*(.+?)(?:\\s*:\\s*\\(|$)/);r.push(n+'\\t'+name+'\\t'+choreo+'\\t'+lv+'\\t'+(ct?ct[1]:'32')+'\\t'+(wl?wl[1]:'4')+'\\t'+(mu?mu[1].trim():'')+'\\t'+url);});var o='Rank\\tName\\tChoreographer\\tLevel\\tCount\\tWall\\tMusic\\tURL\\n'+r.join('\\n');navigator.clipboard.writeText(o).then(function(){alert('Copied '+n+' dances!');}).catch(function(){var ta=document.createElement('textarea');ta.value=o;ta.style.cssText='position:fixed;top:10px;left:10px;width:90%;height:300px;z-index:99999';document.body.appendChild(ta);ta.select();alert('Copy the text manually (Ctrl+C)');});})();";
  
  // Bookmarklet for CopperKnob SEARCH page - extracts all dances from search results
  const copperknobSearchBookmarklet = "javascript:(function(){var links=document.querySelectorAll('a[href*=\"/stepsheets/\"]');var r=[];var seen={};var n=0;links.forEach(function(a){var url=a.href;if(seen[url])return;seen[url]=true;var name=a.textContent.trim();if(!name||name.length<2)return;var parent=a.closest('div')||a.parentElement;var text=parent?parent.textContent:'';var choreo='';var choreoMatch=text.match(/([A-Za-z\\s\\.]+\\([A-Z]{2,3}\\))/);if(choreoMatch)choreo=choreoMatch[1].trim();var ct='32',wl='4',lv='Improver';var countMatch=text.match(/(\\d+)\\s*Count/i);var wallMatch=text.match(/(\\d+)\\s*Wall/i);if(countMatch)ct=countMatch[1];if(wallMatch)wl=wallMatch[1];['Absolute Beginner','High Beginner','Easy Beginner','Beginner','High Improver','Improver','Easy Intermediate','Intermediate','Advanced'].forEach(function(l){if(text.includes(l))lv=l;});var music='';var musicMatch=text.match(/Music:\\s*(.+?)(?:\\s*$|\\n)/i);if(musicMatch)music=musicMatch[1].trim();n++;r.push(n+'\\t'+name+'\\t'+choreo+'\\t'+lv+'\\t'+ct+'\\t'+wl+'\\t'+music+'\\t'+url);});var o='Rank\\tName\\tChoreographer\\tLevel\\tCount\\tWall\\tMusic\\tURL\\n'+r.join('\\n');navigator.clipboard.writeText(o).then(function(){alert('Copied '+n+' dances from this page!');}).catch(function(){var ta=document.createElement('textarea');ta.value=o;ta.style.cssText='position:fixed;top:10px;left:10px;width:90%;height:300px;z-index:99999';document.body.appendChild(ta);ta.select();alert('Copy the text manually (Ctrl+C)');});})();";
  
  // Bookmarklet for LineDancer - uses index.php page structure
  const linedancerBookmarklet = "javascript:(function(){var box=document.getElementById('resultsbox');if(!box){alert('Could not find results. Make sure you are on linedancerweb.com');return;}var orders=box.querySelectorAll('.resultorder');var r=[];var n=0;orders.forEach(function(ord){var holder=ord.nextElementSibling;if(!holder||!holder.classList.contains('resultsholdercharts'))return;n++;var boldEl=holder.querySelector('.resultbold');var name='';var votes=0;if(boldEl){name=boldEl.childNodes[0]?boldEl.childNodes[0].textContent.trim():'';var voteSpan=boldEl.querySelector('span');if(voteSpan){var vMatch=voteSpan.textContent.match(/(\\d+)\\s*votes?/i);if(vMatch)votes=parseInt(vMatch[1]);}}var inners=holder.querySelectorAll('.resultinner');var choreo='';if(inners[0]){var italicEl=inners[0].querySelector('.resultitalic');if(italicEl){var links=italicEl.querySelectorAll('a[href*=\"choreo.php\"]');if(links.length>0){var names=[];links.forEach(function(l){names.push(l.textContent.trim());});choreo=names.join(', ');}}}var smallEl=holder.querySelector('.resultsmall');var ct='32',wl='4';if(smallEl){var txt=smallEl.textContent;var cMatch=txt.match(/(\\d+)\\s*count/i);var wMatch=txt.match(/(\\d+)\\s*wall/i);if(cMatch)ct=cMatch[1];if(wMatch)wl=wMatch[1];}var lvEl=holder.querySelector('.desktoponly');var lv='Improver';if(lvEl){var lvText=lvEl.textContent.trim();['Absolute Beginner','High Beginner','Beginner','High Improver','Improver','Easy Intermediate','Intermediate','Advanced'].forEach(function(l){if(lvText.includes(l))lv=l;});}var linkHolder=holder.nextElementSibling;var url='';if(linkHolder){var linkEl=linkHolder.querySelector('a.resultsanchor');if(linkEl)url=linkEl.href;}r.push(n+'\\t'+name+'\\t'+choreo+'\\t'+lv+'\\t'+ct+'\\t'+wl+'\\t'+votes+'\\t'+url);});var o='Rank\\tName\\tChoreographer\\tLevel\\tCount\\tWall\\tVotes\\tURL\\n'+r.join('\\n');navigator.clipboard.writeText(o).then(function(){alert('Copied '+n+' dances!');}).catch(function(){var ta=document.createElement('textarea');ta.value=o;ta.style.cssText='position:fixed;top:10px;left:10px;width:90%;height:300px;z-index:99999';document.body.appendChild(ta);ta.select();alert('Copy the text manually (Ctrl+C)');});})();";
  
  // Bookmarklet for Everything Line Dance (ELD) - Most Viewed page
  const eldBookmarklet = "javascript:(function(){var items=document.querySelectorAll('li[style*=\"border-color\"]');var r=[];var n=0;items.forEach(function(li){var timeEl=li.querySelector('time');var rank=timeEl?timeEl.textContent.trim().split('\\n')[0]:'';var wrap=li.querySelector('.extra-wrap');if(!wrap)return;n++;var nameLink=wrap.querySelector('a[href*=\"scripts/\"]');var nameText=nameLink?nameLink.textContent.trim():'';var name=nameText.split('|')[0].trim();var choreoLink=wrap.querySelector('a[class*=\"link\"]');var choreo=choreoLink?choreoLink.textContent.trim():'';var viewsSpan=wrap.querySelector('.fa-eye');var views='0';if(viewsSpan&&viewsSpan.parentElement){var vText=viewsSpan.parentElement.textContent;var vMatch=vText.match(/(\\d+)\\/(\\d+)/);if(vMatch)views=vMatch[2];}var ps=wrap.querySelectorAll('p');var ct='32',wl='4',lv='Beginner';ps.forEach(function(p){var b=p.querySelector('b');if(b){var txt=b.textContent;var cMatch=txt.match(/(\\d+)\\s*count/i);var wMatch=txt.match(/(\\d+)\\s*wall/i);if(cMatch)ct=cMatch[1];if(wMatch)wl=wMatch[1];['ABSOLUTE BEGINNER','HIGH BEGINNER','BEGINNER','HIGH IMPROVER','IMPROVER','EASY INTERMEDIATE','INTERMEDIATE','ADVANCED'].forEach(function(l){if(txt.toUpperCase().includes(l))lv=l.split(' ').map(function(w){return w.charAt(0)+w.slice(1).toLowerCase();}).join(' ');});}});var music='';var lastP=ps[ps.length-1];if(lastP&&!lastP.querySelector('b')&&!lastP.querySelector('a')){music=lastP.textContent.trim();}var url=nameLink?nameLink.href:'';r.push(rank+'\\t'+name+'\\t'+choreo+'\\t'+lv+'\\t'+ct+'\\t'+wl+'\\t'+views+'\\t'+music+'\\t'+url);});var o='Rank\\tName\\tChoreographer\\tLevel\\tCount\\tWall\\tViews\\tMusic\\tURL\\n'+r.join('\\n');navigator.clipboard.writeText(o).then(function(){alert('Copied '+n+' dances from ELD!');}).catch(function(){var ta=document.createElement('textarea');ta.value=o;ta.style.cssText='position:fixed;top:10px;left:10px;width:90%;height:300px;z-index:99999';document.body.appendChild(ta);ta.select();alert('Copy the text manually (Ctrl+C)');});})();";
  
  const emptyDance = {
    rank: dances.length + 1,
    name: '',
    choreographer: '',
    url: '',
    views: 0,
    votes: 0,
    count: 32,
    wall: 4,
    level: 'Beginner',
    music: ''
  };
  
  const [newDance, setNewDance] = useState(emptyDance);

  useEffect(() => {
    fetchDances();
  }, [activeSource]);

  const fetchDances = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dance-charts/${activeSource}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDances(data.dances || []);
        setLastUpdated(data.last_updated);
      }
    } catch (error) {
      toast.error('Failed to load dances');
    } finally {
      setLoading(false);
    }
  };

  const saveDances = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Session expired. Please log in again.');
        setSaving(false);
        return;
      }
      const response = await fetch(`${API_URL}/api/admin/dance-charts/${activeSource}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dances })
      });
      
      // Safari-friendly response handling
      let responseText = '';
      try {
        responseText = await response.text();
      } catch (e) {
        console.error('Body read error:', e);
      }
      
      if (response.ok) {
        toast.success(`Saved ${dances.length} dances to ${activeSource}`);
        setLastUpdated(new Date().toISOString());
      } else {
        let errorMsg = 'Failed to save';
        if (responseText) {
          try {
            const errorData = JSON.parse(responseText);
            errorMsg = errorData.detail || errorMsg;
          } catch (e) {}
        }
        toast.error(errorMsg);
      }
    } catch (error) {
      console.error('Save dances error:', error);
      if (error.message?.includes('disturbed') || error.message?.includes('locked') || error.message?.includes('disabled')) {
        toast.warning('Save may have succeeded. Please refresh the page to check.');
      } else {
        toast.error('Failed to save dances. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // DJ Database functions
  const fetchDjDatabase = async () => {
    setDjDbLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dance-database`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDjDbDances(data.dances || []);
        setDjDbCount(data.count || 0);
        setDjDbLastUpdated(data.last_updated);
      }
    } catch (error) {
      toast.error('Failed to load DJ database');
    } finally {
      setDjDbLoading(false);
    }
  };

  const saveDjDatabase = async (mode = 'replace') => {
    if (!djDbBulkText.trim()) {
      toast.error('Please paste dance data first');
      return;
    }
    
    setDjDbSaving(true);
    try {
      const parsed = parseBulkText(djDbBulkText);
      if (parsed.length === 0) {
        toast.error('No valid dances found in the pasted data');
        setDjDbSaving(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dance-database`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ dances: parsed, mode })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${mode === 'append' ? 'Added' : 'Saved'} dances. Total: ${data.count}`);
        setDjDbBulkText('');
        fetchDjDatabase();
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save DJ database');
    } finally {
      setDjDbSaving(false);
    }
  };

  const clearDjDatabase = async () => {
    if (!window.confirm('Are you sure you want to clear all dances from the DJ autocomplete database?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/dance-database`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('DJ database cleared');
        setDjDbDances([]);
        setDjDbCount(0);
        setDjDbLastUpdated(null);
      }
    } catch (error) {
      toast.error('Failed to clear database');
    }
  };

  // Handle ELD import
  const handleEldImport = async () => {
    if (!eldBulkText.trim()) {
      toast.error('Please paste ELD data first');
      return;
    }
    
    setEldSaving(true);
    try {
      const parsed = parseBulkText(eldBulkText);
      if (parsed.length === 0) {
        toast.error('No valid dances found in pasted data');
        setEldSaving(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/admin/top-dances/eld/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ dances: parsed })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Imported ${data.count} dances from ELD!`);
        setEldBulkText('');
        setShowEldImport(false);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to import');
      }
    } catch (error) {
      toast.error(`Failed to import ELD dances: ${error.message}`);
    } finally {
      setEldSaving(false);
    }
  };

  // Load DJ database when tab changes
  useEffect(() => {
    if (activeTab === 'djdb') {
      fetchDjDatabase();
    }
  }, [activeTab]);

  const updateDance = (index, field, value) => {
    const updated = [...dances];
    updated[index] = { ...updated[index], [field]: value };
    setDances(updated);
  };

  const deleteDance = (index) => {
    const updated = dances.filter((_, i) => i !== index);
    // Re-rank remaining dances
    updated.forEach((d, i) => d.rank = i + 1);
    setDances(updated);
  };

  const addDance = () => {
    const danceToAdd = { ...newDance, rank: dances.length + 1 };
    setDances([...dances, danceToAdd]);
    setNewDance({ ...emptyDance, rank: dances.length + 2 });
    setShowAddForm(false);
    toast.success('Dance added - remember to save!');
  };

  const moveDance = (index, direction) => {
    if ((direction === -1 && index === 0) || (direction === 1 && index === dances.length - 1)) return;
    const updated = [...dances];
    const temp = updated[index];
    updated[index] = updated[index + direction];
    updated[index + direction] = temp;
    // Update ranks
    updated.forEach((d, i) => d.rank = i + 1);
    setDances(updated);
  };

  const levelOptions = ['Absolute Beginner', 'Beginner', 'High Beginner', 'Improver', 'High Improver', 'Easy Intermediate', 'Intermediate', 'Advanced'];

  // Parse bulk text into dances
  const parseBulkText = (text) => {
    let lines = text.trim().split('\n').filter(line => line.trim());
    const parsed = [];
    let currentRank = 1;
    let colMap = null;
    
    // Check for header row and build column map for tab-separated data
    if (lines.length > 0) {
      const firstLine = lines[0].toLowerCase();
      if (firstLine.includes('\t') && (firstLine.includes('rank') || firstLine.includes('name') || firstLine.includes('choreographer'))) {
        // Build column map from header
        const headerParts = firstLine.split('\t').map(h => h.trim());
        colMap = {
          rank: headerParts.findIndex(h => h.includes('rank') || h === '#'),
          name: headerParts.findIndex(h => h.includes('name') || h.includes('dance') || h.includes('title')),
          choreographer: headerParts.findIndex(h => h.includes('choreo') || h.includes('artist')),
          level: headerParts.findIndex(h => h.includes('level') || h.includes('difficulty')),
          count: headerParts.findIndex(h => h.includes('count')),
          wall: headerParts.findIndex(h => h.includes('wall')),
          views: headerParts.findIndex(h => h.includes('view')),
          votes: headerParts.findIndex(h => h.includes('vote')),
          music: headerParts.findIndex(h => h.includes('music') || h.includes('song')),
          url: headerParts.findIndex(h => h.includes('url') || h.includes('link'))
        };
        lines = lines.slice(1); // Remove header
      } else if (firstLine.includes('rank') && (firstLine.includes('name') || firstLine.includes('choreographer') || firstLine.includes('level'))) {
        lines = lines.slice(1);
      }
    }
    
    for (const line of lines) {
      // Try to extract dance info from each line
      // Expected formats:
      // 1. "Dance Name - Choreographer (Country)" or
      // 2. "1. Dance Name by Choreographer" or
      // 3. Tab-separated: "Rank\tName\tChoreographer\tLevel\tCount\tWall\tViews\tMusic"
      
      let dance = {
        rank: currentRank,
        name: '',
        choreographer: '',
        url: '',
        views: 0,
        votes: 0,
        count: 32,
        wall: 4,
        level: 'Improver',
        music: ''
      };
      
      // Check if tab-separated (full data)
      if (line.includes('\t')) {
        const parts = line.split('\t').map(p => p.trim());
        
        // If we have a column map from header, use position-based parsing
        if (colMap) {
          const getValue = (idx) => idx >= 0 && idx < parts.length ? parts[idx] : '';
          
          dance.rank = colMap.rank >= 0 ? parseInt(getValue(colMap.rank)) || currentRank : currentRank;
          dance.name = getValue(colMap.name) || getValue(1) || '';
          dance.choreographer = getValue(colMap.choreographer) || getValue(2) || '';
          dance.level = getValue(colMap.level) || 'Improver';
          dance.count = colMap.count >= 0 ? parseInt(getValue(colMap.count)) || 32 : 32;
          dance.wall = colMap.wall >= 0 ? parseInt(getValue(colMap.wall)) || 4 : 4;
          dance.views = colMap.views >= 0 ? parseInt(getValue(colMap.views)) || 0 : 0;
          dance.votes = colMap.votes >= 0 ? parseInt(getValue(colMap.votes)) || 0 : 0;
          dance.music = getValue(colMap.music) || '';
          dance.url = getValue(colMap.url) || '';
          
          // Normalize level
          if (dance.level) {
            const levelLower = dance.level.toLowerCase();
            const matchedLevel = levelOptions.find(l => levelLower.includes(l.toLowerCase()));
            if (matchedLevel) dance.level = matchedLevel;
          }
        } else if (parts.length >= 2) {
          // Fallback: no header, try to guess structure
          // Try to parse rank if it's a number
          const maybeRank = parseInt(parts[0]);
          let startIdx = 0;
          if (!isNaN(maybeRank) && maybeRank > 0 && maybeRank <= 100) {
            dance.rank = maybeRank;
            currentRank = maybeRank;
            startIdx = 1;
          }
          
          dance.name = parts[startIdx] || '';
          dance.choreographer = parts[startIdx + 1] || '';
          
          // For data without header, use standard bookmarklet column positions:
          // Rank(0), Name(1), Choreographer(2), Level(3), Count(4), Wall(5), Votes/Views(6), URL(7)
          if (parts.length >= 6) {
            const levelPart = parts[startIdx + 2];
            if (levelPart && levelOptions.some(l => levelPart.toLowerCase().includes(l.toLowerCase()))) {
              dance.level = levelOptions.find(l => levelPart.toLowerCase().includes(l.toLowerCase())) || 'Improver';
            }
            // Count is typically at position startIdx + 3
            const countVal = parseInt(parts[startIdx + 3]);
            if (!isNaN(countVal) && countVal >= 8 && countVal <= 128) {
              dance.count = countVal;
            }
            // Wall is typically at position startIdx + 4
            const wallVal = parseInt(parts[startIdx + 4]);
            if (!isNaN(wallVal) && wallVal >= 1 && wallVal <= 8) {
              dance.wall = wallVal;
            }
            // Votes/Views at position startIdx + 5
            const votesVal = parseInt(parts[startIdx + 5]);
            if (!isNaN(votesVal)) {
              if (activeSource === 'copperknob') {
                dance.views = votesVal;
              } else {
                dance.votes = votesVal;
              }
            }
            // URL at position startIdx + 6 or later
            for (let i = startIdx + 6; i < parts.length; i++) {
              if (parts[i].startsWith('http://') || parts[i].startsWith('https://')) {
                dance.url = parts[i];
                break;
              }
            }
            // Music - check remaining parts
            for (let i = startIdx + 5; i < parts.length; i++) {
              const part = parts[i];
              if (part && !part.startsWith('http') && !/^\d+$/.test(part) && part.length > 3) {
                dance.music = part;
                break;
              }
            }
          }
        }
      } else {
        // Single line format - try to parse name and choreographer
        let cleanLine = line.trim();
        
        // Remove leading rank number if present (e.g., "1.", "1)", "#1")
        cleanLine = cleanLine.replace(/^[#]?\d+[\.\)\s]+/, '');
        
        // Try "Name - Choreographer" format
        if (cleanLine.includes(' - ')) {
          const [namePart, ...rest] = cleanLine.split(' - ');
          dance.name = namePart.trim();
          dance.choreographer = rest.join(' - ').trim();
        }
        // Try "Name by Choreographer" format
        else if (cleanLine.toLowerCase().includes(' by ')) {
          const byIndex = cleanLine.toLowerCase().indexOf(' by ');
          dance.name = cleanLine.substring(0, byIndex).trim();
          dance.choreographer = cleanLine.substring(byIndex + 4).trim();
        }
        // Just use as name
        else {
          dance.name = cleanLine;
        }
        
        // Extract level from choreographer string if present
        for (const level of levelOptions) {
          if (dance.choreographer.toLowerCase().includes(level.toLowerCase())) {
            dance.level = level;
            dance.choreographer = dance.choreographer.replace(new RegExp(level, 'i'), '').trim();
          }
        }
        
        // Clean up choreographer - remove trailing punctuation
        dance.choreographer = dance.choreographer.replace(/[,\-]+$/, '').trim();
      }
      
      if (dance.name) {
        parsed.push(dance);
        currentRank++;
      }
    }
    
    return parsed;
  };

  const handleBulkTextChange = (text) => {
    setBulkText(text);
    if (text.trim()) {
      const preview = parseBulkText(text);
      setImportPreview(preview);
    } else {
      setImportPreview([]);
    }
  };

  const applyBulkImport = (mode) => {
    if (importPreview.length === 0) {
      toast.error('No dances to import');
      return;
    }
    
    if (mode === 'replace') {
      // Replace all dances
      setDances(importPreview.map((d, i) => ({ ...d, rank: i + 1 })));
      toast.success(`Replaced with ${importPreview.length} dances - remember to save!`);
    } else {
      // Append to existing
      const newDances = [...dances, ...importPreview.map((d, i) => ({ ...d, rank: dances.length + i + 1 }))];
      setDances(newDances);
      toast.success(`Added ${importPreview.length} dances - remember to save!`);
    }
    
    setShowBulkImport(false);
    setBulkText('');
    setImportPreview([]);
  };

  // Download sample CSV template
  const downloadSampleCSV = () => {
    const header = 'Rank,Name,Choreographer,Level,Count,Wall,Views,Music,URL';
    const sample1 = '1,Role Model,Maddison Glover (AUS),High Improver,48,4,,Be Her - Ella Langley,https://www.copperknob.co.uk/stepsheets/role-model';
    const sample2 = '2,Do Si Dough,Maddison Glover & Jo Thompson,Beginner,32,4,,Do Si Dough - Josiah Siska,';
    const sample3 = '3,Dangerous Games,Maggie Gallagher (UK),Intermediate,32,4,,Dangerous Games - Justice B.,';
    
    const csv = [header, sample1, sample2, sample3].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dance_chart_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export current dances to CSV
  const exportToCSV = () => {
    if (dances.length === 0) {
      toast.error('No dances to export');
      return;
    }
    
    // Only include Votes column for LineDancer
    const header = activeSource === 'linedancer' 
      ? 'Rank,Name,Choreographer,Level,Count,Wall,Votes,Music,URL'
      : 'Rank,Name,Choreographer,Level,Count,Wall,Music,URL';
    
    const rows = dances.map(d => {
      const baseCols = `${d.rank},"${(d.name || '').replace(/"/g, '""')}","${(d.choreographer || '').replace(/"/g, '""')}",${d.level || 'Improver'},${d.count || 32},${d.wall || 4}`;
      if (activeSource === 'linedancer') {
        return `${baseCols},${d.votes || 0},"${(d.music || '').replace(/"/g, '""')}","${d.url || ''}"`;
      }
      return `${baseCols},"${(d.music || '').replace(/"/g, '""')}","${d.url || ''}"`;
    });
    
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeSource}_chart_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  // Parse CSV file
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) return []; // Need at least header + 1 row
    
    // Parse header to find column indices
    const header = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/["']/g, ''));
    
    const colMap = {
      rank: header.findIndex(h => h.includes('rank') || h === '#'),
      name: header.findIndex(h => h.includes('name') || h.includes('dance') || h.includes('title')),
      choreographer: header.findIndex(h => h.includes('choreo') || h.includes('artist') || h.includes('by')),
      level: header.findIndex(h => h.includes('level') || h.includes('difficulty')),
      count: header.findIndex(h => h.includes('count')),
      wall: header.findIndex(h => h.includes('wall')),
      views: header.findIndex(h => h.includes('view')),
      votes: header.findIndex(h => h.includes('vote')),
      music: header.findIndex(h => h.includes('music') || h.includes('song')),
      url: header.findIndex(h => h.includes('url') || h.includes('link'))
    };
    
    const parsed = [];
    
    for (let i = 1; i < lines.length; i++) {
      // Handle CSV with quoted fields
      const row = [];
      let current = '';
      let inQuotes = false;
      
      for (const char of lines[i]) {
        if (char === '"' && !inQuotes) {
          inQuotes = true;
        } else if (char === '"' && inQuotes) {
          inQuotes = false;
        } else if (char === ',' && !inQuotes) {
          row.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      row.push(current.trim());
      
      if (row.length < 2) continue;
      
      const getValue = (idx) => idx >= 0 && idx < row.length ? row[idx].replace(/["']/g, '').trim() : '';
      
      const dance = {
        rank: colMap.rank >= 0 ? parseInt(getValue(colMap.rank)) || i : i,
        name: getValue(colMap.name) || getValue(0),
        choreographer: getValue(colMap.choreographer) || getValue(1),
        level: getValue(colMap.level) || 'Improver',
        count: parseInt(getValue(colMap.count)) || 32,
        wall: parseInt(getValue(colMap.wall)) || 4,
        views: parseInt(getValue(colMap.views)) || 0,
        votes: parseInt(getValue(colMap.votes)) || 0,
        music: getValue(colMap.music) || '',
        url: getValue(colMap.url) || ''
      };
      
      if (dance.name) {
        parsed.push(dance);
      }
    }
    
    return parsed;
  };

  // Handle CSV file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const parsed = parseCSV(text);
      
      if (parsed.length > 0) {
        setImportPreview(parsed);
        setBulkText(`Imported from ${file.name}`);
        toast.success(`Parsed ${parsed.length} dances from CSV`);
      } else {
        toast.error('Could not parse any dances from the CSV file');
      }
    };
    reader.onerror = () => {
      toast.error('Error reading file');
    };
    reader.readAsText(file);
    
    // Reset input so same file can be uploaded again
    event.target.value = '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Dance Charts Manager</h2>
          <p className="text-sm text-slate-500">
            Manage top dance charts and DJ autocomplete database
          </p>
        </div>
        {activeTab === 'top20' && (
          <div className="flex items-center gap-2">
            <Button
              onClick={saveDances}
              disabled={saving}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="save-dances-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      {/* Main Tabs: Top 20 vs DJ Database */}
      <div className="flex gap-2 border-b border-slate-200 pb-2">
        <Button
          variant={activeTab === 'top20' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('top20')}
          className={activeTab === 'top20' ? 'bg-slate-800' : ''}
          data-testid="tab-top20"
        >
          <Trophy className="w-4 h-4 mr-2" />
          Top 20 Charts
        </Button>
        <Button
          variant={activeTab === 'djdb' ? 'default' : 'ghost'}
          onClick={() => setActiveTab('djdb')}
          className={activeTab === 'djdb' ? 'bg-blue-600 hover:bg-blue-700' : ''}
          data-testid="tab-djdb"
        >
          <Music className="w-4 h-4 mr-2" />
          DJ Autocomplete Database
        </Button>
      </div>

      {activeTab === 'top20' ? (
        <>
          {/* Source Tabs */}
          <div className="flex gap-2">
            <Button
              variant={activeSource === 'copperknob' ? 'default' : 'outline'}
              onClick={() => setActiveSource('copperknob')}
              className={activeSource === 'copperknob' ? 'bg-amber-500 hover:bg-amber-600' : ''}
              data-testid="source-copperknob"
            >
              <Trophy className="w-4 h-4 mr-2" />
              CopperKnob
            </Button>
            <Button
              variant={activeSource === 'linedancer' ? 'default' : 'outline'}
              onClick={() => setActiveSource('linedancer')}
              className={activeSource === 'linedancer' ? 'bg-violet-500 hover:bg-violet-600' : ''}
              data-testid="source-linedancer"
            >
              <Trophy className="w-4 h-4 mr-2" />
              LineDancer UK
            </Button>
          </div>

          {/* Last Updated */}
          {lastUpdated && (
            <p className="text-sm text-slate-500">
              Last updated: {new Date(lastUpdated).toLocaleString()}
            </p>
      )}

      {/* Add Dance Button */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-600">{dances.length} dances</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={exportToCSV}
            className="text-slate-500 hover:text-slate-700"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowBookmarklet(true)}
            className="border-green-300 text-green-700 hover:bg-green-50"
            data-testid="auto-fetch-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Auto-Fetch Tool
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowBulkImport(!showBulkImport); setShowAddForm(false); setShowBookmarklet(false); setShowEldImport(false); }}
            data-testid="bulk-import-btn"
            className="border-blue-300 text-blue-700 hover:bg-blue-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowEldImport(!showEldImport); setShowAddForm(false); setShowBookmarklet(false); setShowBulkImport(false); }}
            data-testid="eld-import-btn"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
          >
            <Upload className="w-4 h-4 mr-2" />
            ELD Import
          </Button>
          <Button
            variant="outline"
            onClick={() => { setShowAddForm(!showAddForm); setShowBulkImport(false); setShowBookmarklet(false); setShowEldImport(false); }}
            data-testid="add-dance-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Dance
          </Button>
        </div>
      </div>

      {/* Bookmarklet Instructions Modal */}
      {showBookmarklet && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-green-800">Auto-Fetch Dance Charts</h3>
              <p className="text-sm text-green-600 mt-1">
                Use these bookmarklets to automatically extract dances from source websites
              </p>
            </div>
            <button onClick={() => setShowBookmarklet(false)} className="text-green-400 hover:text-green-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            {/* CopperKnob Most Popular Bookmarklet */}
            <div className="bg-white rounded-lg border border-green-200 p-4">
              <h4 className="font-medium text-amber-700 mb-2 flex items-center">
                <Trophy className="w-4 h-4 mr-2" />
                CopperKnob Top 20 Extractor
              </h4>
              <div className="text-sm text-slate-600 space-y-3">
                <div className="bg-amber-50 border border-amber-200 rounded p-3">
                  <p className="font-medium text-amber-800 mb-2">For Most Popular page only (20 dances)</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(copperknobBookmarklet);
                      alert('Bookmarklet code copied! Now paste it as the URL of a new bookmark.');
                    }}
                    className="mt-2 px-3 py-1 bg-amber-500 text-white rounded text-sm hover:bg-amber-600"
                  >
                    Copy Code
                  </button>
                </div>
                <div className="border-t pt-3">
                  <p className="font-medium">Use on:</p>
                  <a href="https://www.copperknob.co.uk/mostpopular" target="_blank" rel="noopener noreferrer" className="text-amber-600 hover:underline">copperknob.co.uk/mostpopular</a>
                </div>
              </div>
            </div>

            {/* CopperKnob Search Bookmarklet - NEW */}
            <div className="bg-white rounded-lg border border-blue-200 p-4">
              <h4 className="font-medium text-blue-700 mb-2 flex items-center">
                <Search className="w-4 h-4 mr-2" />
                CopperKnob Search Extractor (Full Database)
              </h4>
              <div className="text-sm text-slate-600 space-y-3">
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="font-medium text-blue-800 mb-2">For Search page - extracts 20 dances per page</p>
                  <p className="text-blue-600 text-xs mb-2">Run on multiple pages to build a larger database. Use "Append" mode to add to existing dances.</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(copperknobSearchBookmarklet);
                      alert('Search bookmarklet code copied! Create a bookmark and paste this as the URL.');
                    }}
                    className="mt-2 px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600"
                  >
                    Copy Code
                  </button>
                </div>
                <div className="border-t pt-3">
                  <p className="font-medium">How to use:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Go to <a href="https://www.copperknob.co.uk/search?Search=&Level=Beginner" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">CopperKnob Search</a></li>
                    <li>Filter by level (e.g., Beginner, Improver, Intermediate)</li>
                    <li>Click the bookmarklet to copy dances from current page</li>
                    <li>Paste here using "Append" to add to existing data</li>
                    <li>Click "Next" on CopperKnob and repeat for more pages</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* LineDancer Bookmarklet */}
            <div className="bg-white rounded-lg border border-green-200 p-4">
              <h4 className="font-medium text-violet-700 mb-2 flex items-center">
                <Trophy className="w-4 h-4 mr-2" />
                LineDancer UK Extractor
              </h4>
              <div className="text-sm text-slate-600 space-y-3">
                <div className="bg-violet-50 border border-violet-200 rounded p-3">
                  <p className="font-medium text-violet-800 mb-2">Option 1: Manual Bookmark (Recommended)</p>
                  <ol className="space-y-1 text-violet-700">
                    <li>1. Right-click your bookmarks bar → "Add page" or "Add bookmark"</li>
                    <li>2. Name it "Get LineDancer"</li>
                    <li>3. For URL, click "Copy Code" below and paste:</li>
                  </ol>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(linedancerBookmarklet);
                      alert('Bookmarklet code copied! Now paste it as the URL of a new bookmark.');
                    }}
                    className="mt-2 px-3 py-1 bg-violet-500 text-white rounded text-sm hover:bg-violet-600"
                  >
                    Copy Code
                  </button>
                </div>
                <div>
                  <p className="font-medium mb-1">Option 2: Drag to bookmarks bar:</p>
                  <a
                    href={linedancerBookmarklet}
                    onClick={(e) => e.preventDefault()}
                    draggable="true"
                    className="inline-flex items-center px-4 py-2 bg-violet-500 text-white rounded-lg font-medium cursor-move hover:bg-violet-600 shadow-md"
                  >
                    <Trophy className="w-4 h-4 mr-2" />
                    Get LineDancer
                  </a>
                </div>
                <div className="border-t pt-3">
                  <p className="font-medium">Then:</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Go to <a href="https://www.linedancerweb.com/index.php" target="_blank" rel="noopener noreferrer" className="text-violet-600 hover:underline">linedancerweb.com</a></li>
                    <li>Click the bookmarklet</li>
                    <li>Come back here and paste (Ctrl+V) in Bulk Import</li>
                  </ol>
                </div>
              </div>
            </div>

            {/* ELD Most Viewed Bookmarklet - NEW */}
            <div className="bg-white rounded-lg border border-emerald-200 p-4">
              <h4 className="font-medium text-emerald-700 mb-2 flex items-center">
                <Trophy className="w-4 h-4 mr-2" />
                ELD Most Viewed Extractor
              </h4>
              <div className="text-sm text-slate-600 space-y-3">
                <div className="bg-emerald-50 border border-emerald-200 rounded p-3">
                  <p className="font-medium text-emerald-800 mb-2">For Everything Line Dance - Most Viewed Scripts</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(eldBookmarklet);
                      alert('ELD bookmarklet code copied! Now paste it as the URL of a new bookmark.');
                    }}
                    className="mt-2 px-3 py-1 bg-emerald-500 text-white rounded text-sm hover:bg-emerald-600"
                  >
                    Copy Code
                  </button>
                </div>
                <div className="border-t pt-3">
                  <p className="font-medium">How to use:</p>
                  <ol className="list-decimal list-inside space-y-1 text-xs">
                    <li>Go to <a href="https://www.everythinglinedance.com/scripts-stats.php" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">ELD Most Viewed Scripts</a></li>
                    <li>Click the bookmarklet to copy dances</li>
                    <li>Come back here and paste in "ELD Import" section below</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-green-100 rounded p-3 text-sm text-green-800">
            <strong>How it works:</strong> The bookmarklet runs on the source website, extracts all dance data, and copies it to your clipboard in the correct format. Then you just paste it here!
          </div>

          {/* Automated Bulk Fetcher */}
        </div>
      )}

      {/* Bulk Import Form */}
      {showBulkImport && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-blue-800">Bulk Import Dances</h3>
              <p className="text-sm text-blue-600 mt-1">
                Upload a CSV file or paste dance data directly
              </p>
            </div>
            <button onClick={() => setShowBulkImport(false)} className="text-blue-400 hover:text-blue-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* CSV Upload */}
          <div className="bg-white rounded-lg border-2 border-dashed border-blue-300 p-4">
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <Upload className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-sm font-medium text-blue-800">Upload CSV File</p>
                <p className="text-xs text-blue-600 mt-1">Columns: Name, Choreographer, Level, Count, Wall, Music, URL</p>
              </div>
              <div className="flex flex-col gap-2">
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileUpload}
                    className="hidden"
                    data-testid="csv-upload-input"
                  />
                  <span className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <Upload className="w-4 h-4 mr-2" />
                    Choose CSV
                  </span>
                </label>
                <button
                  onClick={downloadSampleCSV}
                  className="text-xs text-blue-600 hover:text-blue-800 underline"
                >
                  Download template
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex-1 h-px bg-blue-200"></div>
            <span className="text-sm text-blue-500 font-medium">OR paste text</span>
            <div className="flex-1 h-px bg-blue-200"></div>
          </div>
          
          <div className="bg-white/50 rounded p-3 text-xs text-blue-700 space-y-1">
            <p><strong>Format 1 (Simple):</strong> Dance Name - Choreographer (one per line)</p>
            <p><strong>Format 2 (Numbered):</strong> 1. Dance Name by Choreographer</p>
            <p><strong>Format 3 (Tab-separated):</strong> Name [tab] Choreographer [tab] Level [tab] Count [tab] Wall [tab] Music</p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-blue-800 mb-2">Paste dance data:</label>
            <textarea
              value={bulkText}
              onChange={(e) => handleBulkTextChange(e.target.value)}
              placeholder="Paste dance list here...

Example:
Role Model - Maddison Glover (AUS)
Do Si Dough - Maddison Glover & Jo Thompson
Dangerous Games - Maggie Gallagher (UK)"
              className="w-full h-32 p-3 border border-blue-300 rounded-lg text-sm font-mono text-slate-900 bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              data-testid="bulk-import-textarea"
            />
          </div>
          
          {/* Preview */}
          {importPreview.length > 0 && (
            <div className="bg-white rounded-lg border border-blue-200 p-3">
              <h4 className="font-medium text-blue-800 mb-2">Preview ({importPreview.length} dances detected):</h4>
              <div className="max-h-48 overflow-y-auto space-y-1">
                {importPreview.slice(0, 10).map((dance, i) => (
                  <div key={i} className="text-sm flex items-center gap-2 py-1 border-b border-blue-100 last:border-0">
                    <span className="w-6 h-6 rounded bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold">
                      {dance.rank}
                    </span>
                    <span className="font-medium text-slate-800">{dance.name}</span>
                    <span className="text-slate-500">-</span>
                    <span className="text-slate-600">{dance.choreographer || 'Unknown'}</span>
                    <span className="ml-auto px-2 py-0.5 rounded text-xs bg-slate-100 text-slate-600">{dance.level}</span>
                  </div>
                ))}
                {importPreview.length > 10 && (
                  <p className="text-sm text-blue-600 pt-2">...and {importPreview.length - 10} more</p>
                )}
              </div>
            </div>
          )}
          
          <div className="flex gap-2">
            <Button 
              onClick={() => applyBulkImport('replace')} 
              disabled={importPreview.length === 0}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Replace All ({importPreview.length})
            </Button>
            <Button 
              onClick={() => applyBulkImport('append')} 
              disabled={importPreview.length === 0}
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-100"
            >
              Append to Existing
            </Button>
            <Button variant="outline" onClick={() => { setShowBulkImport(false); setBulkText(''); setImportPreview([]); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* ELD Import Form */}
      {showEldImport && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h3 className="font-semibold text-emerald-800">Import ELD Most Viewed Dances</h3>
              <p className="text-sm text-emerald-600 mt-1">
                Paste the data from the ELD bookmarklet to import most viewed dances
              </p>
            </div>
            <button onClick={() => { setShowEldImport(false); setEldBulkText(''); }} className="text-emerald-400 hover:text-emerald-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-white rounded-lg p-4 border border-emerald-200">
            <div className="space-y-3">
              <div className="text-sm text-slate-600">
                <p className="font-medium">Steps to import:</p>
                <ol className="list-decimal list-inside mt-1 space-y-1">
                  <li>Go to <a href="https://www.everythinglinedance.com/scripts-stats.php" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">ELD Most Viewed Scripts</a></li>
                  <li>Click the "ELD Most Viewed Extractor" bookmarklet (from Auto-Fetch Tool above)</li>
                  <li>Paste the copied data below</li>
                </ol>
              </div>
              
              <Textarea
                value={eldBulkText}
                onChange={(e) => setEldBulkText(e.target.value)}
                placeholder="Paste ELD bookmarklet data here (tab-separated)..."
                className="min-h-32 font-mono text-xs"
                data-testid="eld-import-textarea"
              />
              
              {eldBulkText.trim() && (
                <div className="bg-emerald-100 rounded p-2 text-sm text-emerald-800">
                  Preview: {parseBulkText(eldBulkText).length} dances detected
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button 
              onClick={handleEldImport} 
              disabled={eldSaving || !eldBulkText.trim()}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="eld-import-save"
            >
              {eldSaving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Importing...</> : 'Import to ELD Chart'}
            </Button>
            <Button variant="outline" onClick={() => { setShowEldImport(false); setEldBulkText(''); }}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Add Dance Form */}
      {showAddForm && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-4">
          <h3 className="font-semibold text-green-800">Add New Dance</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <Input
                value={newDance.name}
                onChange={(e) => setNewDance({ ...newDance, name: e.target.value })}
                placeholder="Dance name"
                data-testid="new-dance-name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Choreographer *</label>
              <Input
                value={newDance.choreographer}
                onChange={(e) => setNewDance({ ...newDance, choreographer: e.target.value })}
                placeholder="Choreographer name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Level</label>
              <select
                value={newDance.level}
                onChange={(e) => setNewDance({ ...newDance, level: e.target.value })}
                className="w-full border border-slate-300 rounded-md px-3 py-2"
              >
                {levelOptions.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Count</label>
              <Input
                type="number"
                value={newDance.count}
                onChange={(e) => setNewDance({ ...newDance, count: parseInt(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Wall</label>
              <Input
                type="number"
                value={newDance.wall}
                onChange={(e) => setNewDance({ ...newDance, wall: parseInt(e.target.value) || 0 })}
              />
            </div>
            {activeSource === 'linedancer' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Votes</label>
              <Input
                type="number"
                value={newDance.votes}
                onChange={(e) => setNewDance({ ...newDance, votes: parseInt(e.target.value) || 0 })}
              />
            </div>
            )}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Music</label>
              <Input
                value={newDance.music}
                onChange={(e) => setNewDance({ ...newDance, music: e.target.value })}
                placeholder="Song - Artist"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Step Sheet URL</label>
              <Input
                value={newDance.url}
                onChange={(e) => setNewDance({ ...newDance, url: e.target.value })}
                placeholder="https://..."
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={addDance} disabled={!newDance.name || !newDance.choreographer} className="bg-green-600 hover:bg-green-700">
              Add Dance
            </Button>
            <Button variant="outline" onClick={() => setShowAddForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Dance List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-violet-500" />
          <span className="ml-2 text-slate-500">Loading dances...</span>
        </div>
      ) : (
        <div className="space-y-2">
          {dances.map((dance, index) => (
            <div
              key={`${dance.rank}-${dance.name}`}
              className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors"
              data-testid={`dance-row-${index}`}
            >
              <div className="flex items-start gap-4">
                {/* Rank & Move Buttons */}
                <div className="flex flex-col items-center gap-1">
                  <button
                    onClick={() => moveDance(index, -1)}
                    disabled={index === 0}
                    className="text-slate-500 hover:text-slate-600 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4 rotate-90" />
                  </button>
                  <span className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${
                    dance.rank === 1 ? 'bg-yellow-400 text-yellow-900' :
                    dance.rank === 2 ? 'bg-slate-300 text-slate-800' :
                    dance.rank === 3 ? 'bg-amber-600 text-white' :
                    'bg-slate-600 text-white'
                  }`}>
                    {dance.rank}
                  </span>
                  <button
                    onClick={() => moveDance(index, 1)}
                    disabled={index === dances.length - 1}
                    className="text-slate-500 hover:text-slate-600 disabled:opacity-30"
                  >
                    <ChevronLeft className="w-4 h-4 -rotate-90" />
                  </button>
                </div>

                {/* Editable Fields */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Name</label>
                    <Input
                      value={dance.name}
                      onChange={(e) => updateDance(index, 'name', e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Choreographer</label>
                    <Input
                      value={dance.choreographer}
                      onChange={(e) => updateDance(index, 'choreographer', e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Level</label>
                    <select
                      value={dance.level}
                      onChange={(e) => updateDance(index, 'level', e.target.value)}
                      className="w-full h-8 text-sm border border-slate-300 rounded-md px-2"
                    >
                      {levelOptions.map(level => (
                        <option key={level} value={level}>{level}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <label className="block text-xs text-slate-500 mb-1">Count</label>
                      <Input
                        type="number"
                        value={dance.count || ''}
                        onChange={(e) => updateDance(index, 'count', parseInt(e.target.value) || 0)}
                        className="h-8 text-sm"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-slate-500 mb-1">Wall</label>
                      <Input
                        type="number"
                        value={dance.wall || ''}
                        onChange={(e) => updateDance(index, 'wall', parseInt(e.target.value) || 0)}
                        className="h-8 text-sm"
                      />
                    </div>
                  </div>
                  {activeSource === 'linedancer' && (
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">Votes</label>
                    <Input
                      type="number"
                      value={dance.votes || ''}
                      onChange={(e) => updateDance(index, 'votes', parseInt(e.target.value) || 0)}
                      className="h-8 text-sm"
                    />
                  </div>
                  )}
                  <div className={activeSource === 'copperknob' ? 'lg:col-span-3' : 'lg:col-span-2'}>
                    <label className="block text-xs text-slate-500 mb-1">Music</label>
                    <Input
                      value={dance.music || ''}
                      onChange={(e) => updateDance(index, 'music', e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-500 mb-1">URL</label>
                    <div className="flex gap-1">
                      <Input
                        value={dance.url || ''}
                        onChange={(e) => updateDance(index, 'url', e.target.value)}
                        className="h-8 text-sm"
                      />
                      {dance.url && (
                        <a href={dance.url} target="_blank" rel="noopener noreferrer" className="p-2 text-slate-500 hover:text-violet-600">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                {/* Delete Button */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Dance</AlertDialogTitle>
                      <AlertDialogDescription>
                        Remove "{dance.title}" from the chart? Remember to save changes after.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => deleteDance(index)} className="bg-red-600 hover:bg-red-700">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Save Reminder */}
      {dances.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between">
          <p className="text-amber-800 text-sm">
            Remember to click "Save Changes" to update the live site
          </p>
          <Button
            onClick={saveDances}
            disabled={saving}
            className="bg-amber-500 hover:bg-amber-600"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Save
          </Button>
        </div>
      )}
        </>
      ) : (
        /* DJ Autocomplete Database Tab */
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
              <Music className="w-5 h-5" />
              DJ Autocomplete Database
            </h3>
            <p className="text-sm text-blue-700 mb-2">
              This is a separate database used for autocomplete suggestions when dancers request dances during DJ Live sessions. 
              Import a larger set of dances (e.g., top 500-1000 by rating) for better autocomplete coverage.
            </p>
            <p className="text-sm text-blue-600">
              <strong>Note:</strong> This is independent from the Top 20 charts displayed on your website.
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 bg-slate-100 rounded-lg p-4">
            <div>
              <p className="text-2xl font-bold text-slate-800">{djDbCount}</p>
              <p className="text-sm text-slate-500">Dances in database</p>
            </div>
            {djDbLastUpdated && (
              <div className="border-l pl-4">
                <p className="text-sm text-slate-500">Last updated</p>
                <p className="text-sm font-medium text-slate-700">{new Date(djDbLastUpdated).toLocaleString()}</p>
              </div>
            )}
            {djDbCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearDjDatabase}
                className="ml-auto border-red-300 text-red-600 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Clear Database
              </Button>
            )}
          </div>

          {/* Import Instructions */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-800 mb-2">How to Import Dances</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-green-700">
              <li>Go to <a href="https://www.copperknob.co.uk/search?Order=Rating" target="_blank" rel="noopener noreferrer" className="text-green-600 underline">CopperKnob sorted by Rating</a></li>
              <li>Create a bookmark with this code (same as Top 20 search bookmarklet):</li>
            </ol>
            <button
              onClick={() => {
                navigator.clipboard.writeText(copperknobSearchBookmarklet);
                toast.success('Bookmarklet code copied!');
              }}
              className="mt-2 px-3 py-1 bg-green-600 text-white rounded text-sm hover:bg-green-700"
            >
              Copy Bookmarklet Code
            </button>
            <ol className="list-decimal list-inside space-y-1 text-sm text-green-700 mt-3" start={3}>
              <li>Run the bookmarklet on each page</li>
              <li>Paste here and click <strong>Append</strong> to add to existing dances</li>
              <li>Repeat for multiple pages (25 pages = ~500 dances)</li>
            </ol>
          </div>

          {/* Bulk Import Area */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-700">
              Paste dance data from bookmarklet
            </label>
            <Textarea
              value={djDbBulkText}
              onChange={(e) => setDjDbBulkText(e.target.value)}
              placeholder="Paste tab-separated data from CopperKnob bookmarklet here..."
              className="h-32 font-mono text-sm"
              data-testid="djdb-bulk-textarea"
            />
            <div className="flex gap-2">
              <Button
                onClick={() => saveDjDatabase('append')}
                disabled={djDbSaving || !djDbBulkText.trim()}
                className="bg-green-600 hover:bg-green-700"
                data-testid="djdb-append-btn"
              >
                {djDbSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Append to Database
              </Button>
              <Button
                onClick={() => saveDjDatabase('replace')}
                disabled={djDbSaving || !djDbBulkText.trim()}
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="djdb-replace-btn"
              >
                Replace All
              </Button>
            </div>
          </div>

          {/* Preview of some dances */}
          {djDbDances.length > 0 && (
            <div className="bg-white rounded-lg border p-4">
              <h4 className="font-medium text-slate-700 mb-2">Sample dances in database</h4>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {djDbDances.slice(0, 20).map((dance, i) => (
                  <div key={i} className="text-sm text-slate-600 flex items-center gap-2">
                    <span className="text-slate-500 w-6">{i + 1}.</span>
                    <span className="font-medium">{dance.name}</span>
                    {dance.choreographer && <span className="text-slate-500">- {dance.choreographer}</span>}
                    {dance.level && <span className="text-xs bg-slate-100 px-1 rounded">{dance.level}</span>}
                  </div>
                ))}
                {djDbDances.length > 20 && (
                  <p className="text-sm text-slate-500 mt-2">...and {djDbDances.length - 20} more</p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DanceChartsManager;
